/**
 * ProfilePage — current user details + logout + (platform root only)
 * a password-reset support action for any tenant user.
 */
import React, { useEffect, useState } from 'react';
import AppHeader from './layout/AppHeader';
import { useAuth } from '../context/AuthContext';
import PasswordField, { checkPasswordClient } from './auth/PasswordField';
import MfaPanel from './MfaPanel';
import GlassSelect from './GlassSelect';
import ChangePasswordDialog from './ChangePasswordDialog';
import FoundryAccessCheck from './FoundryAccessCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PlatformResetSection() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/users`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setUsers(data.users || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const { ok: pwOk } = checkPasswordClient(newPw);
  const canSubmit = target && pwOk && reason.trim().length >= 3 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${target.id}/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPw, reason }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        let detail = 'Reset failed';
        if (body.detail) {
          if (typeof body.detail === 'string') detail = body.detail;
          else if (body.detail.messages) detail = body.detail.messages.join(' ');
        }
        throw new Error(detail);
      }
      const data = await res.json();
      setMsg(`Password reset for ${data.user_email} (${data.user_role}).`);
      setTarget(null); setNewPw(''); setReason('');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel" style={{ marginTop: 16 }} data-testid="platform-password-reset-panel">
      <div className="panel-header">Password reset (support action)</div>
      <div className="panel-body">
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Reset a tenant user's password as a support / recovery action. Every reset is audit-logged
          with the reason you provide. Platform Support Admin accounts cannot be targeted here — use
          root-admin management for those.
        </p>

        {loading && <div style={{ fontSize: 12 }}>Loading tenant users…</div>}

        {!loading && (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="form-group">
              <label>Target user</label>
              <GlassSelect
                value={target?.id || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  setTarget(users.find((u) => u.id === id) || null);
                }}
                data-testid="reset-target-select"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
              >
                <option value="">— choose a user —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} ({u.role})
                  </option>
                ))}
              </GlassSelect>
            </div>

            <PasswordField
              id="reset-new-password"
              value={newPw}
              onChange={setNewPw}
              label="New password"
              testIdPrefix="reset-password"
            />

            <div className="form-group">
              <label htmlFor="reset-reason">Reason (required)</label>
              <input
                id="reset-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. user locked out, verified via side channel"
                data-testid="reset-reason-input"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
              />
            </div>

            {error && <div className="auth-error" data-testid="reset-error">{error}</div>}
            {msg && <div className="panel-body" data-testid="reset-success" style={{ color: 'var(--success)', padding: '8px 12px', border: '1px solid var(--success)', borderRadius: 6, fontSize: 12 }}>{msg}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!canSubmit}
              data-testid="reset-submit"
              style={{ alignSelf: 'flex-start' }}
            >
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout } = useAuth();
  // Iter 32l — proactive Change Password modal. Backend
  // (`POST /api/users/me/password`) is unchanged; we just expose
  // a normal entry point in addition to the force-rotation banner.
  const [showChangePw, setShowChangePw] = useState(false);
  if (!user) return null;

  return (
    <>
      <AppHeader active="profile" />
      <div className="page-shell" data-testid="profile-page">
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '8px 0 6px' }}>Profile</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Your account details and session controls.
        </p>

        <div className="panel" data-testid="profile-card">
          <div className="panel-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', rowGap: 6, columnGap: 16, fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</span>
              <span data-testid="profile-name">{user.name || '—'}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</span>
              <span data-testid="profile-email">{user.email}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</span>
              <span data-testid="profile-role">{user.role_label}</span>
              {user.tenant_id && (
                <>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tenant</span>
                  <span data-testid="profile-tenant">{user.tenant_id}</span>
                </>
              )}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-outline btn-sm" onClick={logout} data-testid="profile-logout">
                Log out
              </button>
              {/* Iter 32l — always-available self-service Change
                  Password. Available to every authenticated user
                  (tenant Owner / Operator / Platform Root logged in
                  normally). The modal posts to the existing
                  /api/users/me/password endpoint. */}
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowChangePw(true)}
                data-testid="profile-change-password"
                title="Set a new password for your account. Other live sessions will be signed out."
              >
                Change password
              </button>
            </div>
          </div>
        </div>

        {user.is_platform_root && <PlatformResetSection />}
        {!user.is_platform_root && <FoundryAccessCheck user={user} />}

        {/* MFA is available to every user regardless of role */}
        <MfaPanel />

        {/* About / Legal — calm, compact panel at the bottom of Profile.
            Plain text only; no logos. Visible to every authenticated user. */}
        <div className="panel" style={{ marginTop: 16 }} data-testid="about-legal-panel">
          <div className="panel-body">
            <h3 style={{
              margin: 0, marginBottom: 8,
              fontSize: 11, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: 'var(--text-secondary)',
            }}>
              About / Legal
            </h3>
            <dl style={{
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              columnGap: 16, rowGap: 4,
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--text-primary)',
            }}>
              <dt style={{ color: 'var(--text-secondary)' }}>Company</dt>
              <dd style={{ margin: 0 }} data-testid="about-company-name">QUOD-Tech Ltd</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Company number</dt>
              <dd style={{ margin: 0 }} data-testid="about-company-number">16984972</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Jurisdiction</dt>
              <dd style={{ margin: 0 }}>Registered in England and Wales</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Registered office</dt>
              <dd style={{ margin: 0 }} data-testid="about-registered-office">
                124 City Road, London, England, EC1V 2NX
              </dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Website</dt>
              <dd style={{ margin: 0 }}>
                <a
                  href="https://www.quod-tech.com"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="about-website"
                  style={{ color: 'var(--brand-blue, #1F3A8A)', textDecoration: 'none' }}
                >
                  www.quod-tech.com
                </a>
              </dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Contact</dt>
              <dd style={{ margin: 0 }}>
                <a
                  href="mailto:info@quod-tech.com"
                  data-testid="about-contact"
                  style={{ color: 'var(--brand-blue, #1F3A8A)', textDecoration: 'none' }}
                >
                  info@quod-tech.com
                </a>
              </dd>
            </dl>
          </div>
        </div>
      </div>
      {showChangePw && (
        <ChangePasswordDialog
          onClose={() => setShowChangePw(false)}
          onSaved={() => { /* keep dialog open on success state */ }}
        />
      )}
    </>
  );
}
